import { makeAutoObservable, runInAction } from 'mobx';
import AuthService from 'services/AuthService';
import Cookies from 'js-cookie';
import { COOKIE_KEYS, COOKIE_OPTIONS } from 'config/cookieConstants';
import { message } from 'antd';
import AdministratorService from 'services/AdministratorService';
import type { CompanyModel } from 'models/company/entities/CompanyModel';
import { isGetCompanyListResponseModel, isLoginResponseModel } from './AuthTypeGuards';
import type { LoginFormModel } from 'models/auth/forms/LoginFormModel';
import type { Confirmation2FAFormModel } from 'models/auth/forms/Confirmation2FAFormModel';
import { PermissionModel } from 'models/auth/entities/PermissionModel';
import { isOwnPermissionsListResponseModel } from 'stores/administrator/AdministratorTypeGuard';

class AuthStore {
    isAuthenticated: boolean = false;
    loading: boolean = false;
    loginProps: any;
    loginToken: string | undefined;
    ownPermissionList: PermissionModel[] = [];
    companyList: CompanyModel[] = [];
    selectedCompany?: CompanyModel;

    constructor() {
        makeAutoObservable(this);

        const token = Cookies.get(COOKIE_KEYS.TOKEN);
        if (token) {
            this.isAuthenticated = true;
        }
    }


    async request2FA(data: LoginFormModel): Promise<boolean> {
        this.loading = true;
        const payload = {
            email: data.email,
        }

        const response = await AuthService.request2FA(payload);

        if (response?.error) {
            message.error('Erro ao enviar o e-mail');
        } else {
            message.success('E-mail enviado com sucesso');
            this.loginProps = data;
        }

        this.loading = false;
        return !!response?.error;
    }

    async login(token: Confirmation2FAFormModel): Promise<boolean> {
        const data = { ...token, ...this.loginProps }

        const response = await AuthService.login(data);

        if (isLoginResponseModel(response)) {
            message.success('Login realizado com sucesso');
            const expireConfiguration = Cookies.get(COOKIE_KEYS.REMEMBER) === 'true' ? COOKIE_OPTIONS.USER_PREFERENCES : COOKIE_OPTIONS.NOT_REMEMBER;
            Cookies.set(COOKIE_KEYS.TOKEN, response.token, expireConfiguration);
            Cookies.set(COOKIE_KEYS.NAME, response.firstName, expireConfiguration);
            Cookies.set(COOKIE_KEYS.EMAIL, response.email, expireConfiguration);
            Cookies.set(COOKIE_KEYS.UUID, response.uuid, expireConfiguration);

            this.isAuthenticated = true;
            this.companyList = response.companies;

            return true;
        } else {
            message.error('Autenticação falhou');
        }
        return false;
    }

    async no2faLogin(payload: LoginFormModel): Promise<boolean> {

        const data = {
            token: '123',
            ...payload,
        }
        const response = await AuthService.login(data);

        if (isLoginResponseModel(response)) {
            message.success('Login realizado com sucesso');
            const expireConfiguration = Cookies.get(COOKIE_KEYS.REMEMBER) === 'true' ? COOKIE_OPTIONS.USER_PREFERENCES : COOKIE_OPTIONS.NOT_REMEMBER;
            Cookies.set(COOKIE_KEYS.TOKEN, response.token, expireConfiguration);
            Cookies.set(COOKIE_KEYS.NAME, response.firstName, expireConfiguration);
            Cookies.set(COOKIE_KEYS.EMAIL, response.email, expireConfiguration);
            Cookies.set(COOKIE_KEYS.UUID, response.uuid, expireConfiguration);

            this.isAuthenticated = true;
            this.companyList = response.companies;

            return true;
        } else {
            message.error('Autenticação falhou');
        }
        return false;
    }


    logout() {
        Object.values(COOKIE_KEYS).forEach(cookieName => {
            Cookies.remove(cookieName);
        });
        this.isAuthenticated = false;
        message.success('Logout realizado com sucesso');
    }

    cleanSelectedCompany() {
        this.selectedCompany = undefined;
        Cookies.remove(COOKIE_KEYS.COMPANY_UUID);
    }

    setSelectedCompany(company: CompanyModel) {
        this.selectedCompany = company;
        Cookies.set(COOKIE_KEYS.COMPANY_UUID, company.uuid);
    }

    async getCompanyList() {
        this.loading = true;

        const response = await AuthService.getCompanyList();

        if (isGetCompanyListResponseModel(response)) {
            this.companyList = response.content;
        } else {
            message.error(response.error);
        }
        this.loading = false
    }


    checkAuthentication() {
        const token = Cookies.get(COOKIE_KEYS.TOKEN)
        if (token) {
            this.isAuthenticated = true;
        } else {
            this.isAuthenticated = false;
        }
    }

    async redefinePass(email: string, isStoom: boolean) {
        this.loading = true;

        const response = await AuthService.redefinePass(email, isStoom);

        runInAction(() => {
            if (response?.error) {
                message.error('Erro ao enviar o e-mail');
            } else {
                message.success('E-mail enviado com sucesso');
            }
            this.loading = false;
        });

        return response;
    }

    async validateToken(token: string, isStoom: boolean) {
        this.loading = true;

        const response = await AuthService.validateToken(token, isStoom);

        runInAction(() => {
            if (response?.error) {
                message.error('Erro ao validar token');
            } else {
                message.success('Token validado com sucesso');
            }
            this.loading = false;
        });

        return response;
    }

    async changePassword(token: string, password: string, isStoom: boolean) {
        this.loading = true;

        const response = await AuthService.changePassword(
            token,
            password,
            isStoom
        );

        runInAction(() => {
            if (response?.error) {
                message.error('Erro ao alterar senha');
            } else {
                message.success('Senha alterada com sucesso com sucesso');
            }
            this.loading = false;
        });

        return response;
    }

    async getOwnPermissionList() {
        const response = await AdministratorService.getOwnPermissionList();
        if (isOwnPermissionsListResponseModel(response)) {
            this.ownPermissionList = response.map(i => new PermissionModel(i));
        } else {
            message.error(response.error);
            this.logout()
        }

        return response;
    }
}

const authStore = new AuthStore();
export default authStore;